/**
 * Permission-set + assignment + junction endpoints, plus teammate listing
 * (used by prompt-sharing flows).
 */

import { ApiError, get, post } from './_shared.ts';

export interface AdminAssignmentGrid {
  members: Array<{
    account_user_id: string;
    display_name: string;
    email: string;
    role: 'owner' | 'admin' | 'user';
  }>;
  connectors: Array<{
    instance_id: string;
    template_name: string;
    display_name: string;
  }>;
  /** Map of `${account_user_id}:${instance_id}` → true if active assignment. */
  assignments: Record<string, boolean>;
}

export function fetchAssignmentGrid(): Promise<AdminAssignmentGrid> {
  return get('/api/admin/assignments');
}

export function setAssignment(
  accountUserId: string,
  instanceId: string,
  assigned: boolean,
): Promise<{ ok: true }> {
  return post('/api/admin/assignments/set', {
    account_user_id: accountUserId,
    connector_instance_id: instanceId,
    assigned,
  });
}

/**
 * D-161 Refinement 4 — direct user × prompt junction soft-delete toggle.
 * Mirrors setAssignment's shape. Backend: /api/admin/assignments/prompt-set.
 */
export function setPromptAssignment(
  accountUserId: string,
  promptInstanceId: string,
  assigned: boolean,
): Promise<{ ok: true }> {
  return post('/api/admin/assignments/prompt-set', {
    account_user_id: accountUserId,
    prompt_instance_id: promptInstanceId,
    assigned,
  });
}

/**
 * D-161 Refinement 4 — direct user × model junction soft-delete toggle.
 * 409 missing_credential surfaces when the model's provider has no active
 * credential row yet; the modal should redirect to /admin/models/credentials.
 */
export function setModelAssignment(
  accountUserId: string,
  modelInstanceId: string,
  assigned: boolean,
): Promise<{ ok: true }> {
  return post('/api/admin/assignments/model-set', {
    account_user_id: accountUserId,
    model_instance_id: modelInstanceId,
    assigned,
  });
}

// ----- D-161 inverse-search + record-page scaffold endpoints (Push #7.5 B) -----

export interface AccessLookupUserViaPS {
  account_user_id: string;
  /** Phase 2 Block 2A close sweep — sys__account_user.short_id (usr-prefix) for
   *  RecordAssignmentsTab to deep-link via /admin/people/:short_id. */
  account_user_short_id: string;
  display_name: string;
  email: string;
  permission_set_id: string;
  /** Phase 2 Block 2A close sweep — sys__permission_set.short_id (pst-prefix) for
   *  /admin/permission-sets/:short_id chips. */
  permission_set_short_id: string;
  permission_set_name: string;
  /** Push #76H — owner-bootstrap PS rows are non-removable (auto-maintained
   *  by migration 0047 triggers). UI checks this for the "Owner Permissions"
   *  pill vs the × remove button. */
  permission_set_kind: string;
  /** Push 4-1d-k-final (2026-07-02) — junction row's short_id from the
   *  corresponding sys__permission_set_*_assignment table. Powers the
   *  D-161 junction-symmetric drill from the WhoHasAccessTo chip to
   *  /admin/permission-set-{connector|prompt|model}/:short_id. */
  junction_short_id: string;
}
export interface AccessLookupUserViaDirect {
  account_user_id: string;
  /** Phase 2 Block 2A close sweep — sys__account_user.short_id (usr-prefix). */
  account_user_short_id: string;
  display_name: string;
  email: string;
  granted_at: string;
  /** Push 4-1d-k-final (2026-07-02) — direct-assignment junction row's
   *  short_id from mcp__connector_assignment / knowledge__prompt_assignment
   *  / llm__model_assignment. Powers the D-161 drill to
   *  /admin/{connector|prompt|model}-assignment/:short_id. */
  junction_short_id: string;
}
export interface AccessLookup {
  users_via_ps: AccessLookupUserViaPS[];
  users_via_direct: AccessLookupUserViaDirect[];
}

export function fetchAccessLookup(
  kind: 'connector' | 'prompt' | 'model' | 'folder',
  id: string,
): Promise<AccessLookup> {
  return get(`/api/admin/access-lookup?kind=${kind}&id=${encodeURIComponent(id)}`);
}

/**
 * Push #76H — junction-symmetric hard DELETE. One client helper for every
 * junction kind so every UI surface uses the same code path.
 *
 * Tagged union — `kind` selects which junction table to drop from. The two
 * FK columns are required per kind. The server is the source of truth on
 * validation; this type just keeps the call sites honest at compile time.
 *
 * Owner-bootstrap PS junctions are SERVER-blocked: the response's
 * `blocked[]` will carry any item whose `permission_set_id` resolves to a
 * `kind='owner_bootstrap'` PS. UI should still render those as a
 * non-clickable pill (not as a button), but the server enforces it
 * regardless as a belt-and-suspenders defense.
 */
export type JunctionRef =
  | { kind: 'user_model'; account_user_id: string; model_instance_id: string }
  | { kind: 'user_connector'; account_user_id: string; connector_instance_id: string }
  | { kind: 'user_prompt'; account_user_id: string; prompt_instance_id: string }
  | { kind: 'ps_user'; permission_set_id: string; account_user_id: string }
  | { kind: 'ps_model'; permission_set_id: string; model_instance_id: string }
  | { kind: 'ps_connector'; permission_set_id: string; connector_instance_id: string }
  | { kind: 'ps_prompt'; permission_set_id: string; prompt_instance_id: string };

export interface JunctionDeleteResult {
  deleted: number;
  blocked: Array<{
    ref: JunctionRef;
    reason: 'owner_bootstrap_locked' | 'invalid_ref';
  }>;
}

/**
 * Delete one or more junction records in a single transaction. Bulk-safe;
 * a single-row remove is just `items: [oneRef]`. Server caps `items.length`
 * at 200 to keep "Remove all" from running away.
 */
export function deleteJunctions(items: JunctionRef[]): Promise<JunctionDeleteResult> {
  return post('/api/admin/junction-delete', { items });
}

// ---------------------------------------------------------------------
// Connector Sets (Phase 1a, basic CRUD)
// ---------------------------------------------------------------------

export interface ConnectorSet {
  id: string;
  /** Phase 2 Block 2A close sweep — sys__permission_set.short_id (pst-prefix)
   *  for /admin/permission-sets/:short_id deep-link from list rows. */
  short_id: string;
  name: string;
  description: string | null;
  /** D-147 lock state: owner_bootstrap is locked from edits + delete; admin
   * uses Reset Bootstrap to refresh from current catalog. standard_members is
   * editable. admin_curated is full CRUD. */
  kind: 'owner_bootstrap' | 'standard_members' | 'admin_curated';
  /** Migration 0062: when true, every new invitee is auto-added to this PS
   * on accept-invite. True by default for the bootstrap "Everyone in this
   * workspace" set; admins can flip on any custom set. */
  auto_assign_new_members: boolean;
  created_at: string;
  user_count: number;
  connector_count: number;
  /** Migration 0039: permission sets carry knowledge + models too. */
  prompt_count: number;
  model_count: number;
}

export interface ConnectorSetUser {
  account_user_id: string;
  /** Push 4-1e-cleanup-fu2 (2026-07-08): sys__account_user.short_id
   *  (usr-prefix) so the PS-detail user list can drill via canonical
   *  /admin/people/:short_id URL. */
  account_user_short_id: string;
  display_name: string;
  email: string;
  role: 'owner' | 'admin' | 'user';
}

export interface ConnectorSetConnector {
  instance_id: string;
  display_name: string;
  template_name: string;
  enabled: boolean;
}

/** Migration 0037: a permission set carries prompts. */
export interface ConnectorSetPrompt {
  instance_id: string;
  display_name: string;
  visibility: string;
  category: string | null;
}

/** Migration 0037: a permission set carries models. */
export interface ConnectorSetModel {
  instance_id: string;
  display_name: string;
  provider_slug: string | null;
  is_workspace_default: boolean;
}

/** Push 4-1 folder architecture: a permission set carries folders (Library
 *  pillar). Assignment lives in sys__permission_set_folder_assignment; docs
 *  inherit via the folder cascade RLS predicate (user_can_see_folder). */
export interface ConnectorSetFolder {
  folder_id: string;
  display_name: string;
  description: string | null;
}

export interface ConnectorSetDetail {
  set: {
    id: string;
    name: string;
    description: string | null;
    /** D-147 permission set kind. Backend returns it; UI uses it to lock
     *  Owner Bootstrap edits per D-161 Refinement 3. */
    kind: 'owner_bootstrap' | 'standard_members' | 'admin_curated';
    /** Migration 0062: auto-assignment toggle. See ConnectorSet. */
    auto_assign_new_members: boolean;
    created_at: string;
  };
  users: ConnectorSetUser[];
  connectors: ConnectorSetConnector[];
  /** Migration 0037: prompts the admin added to this permission set. */
  prompts: ConnectorSetPrompt[];
  /** Migration 0037: models the admin added to this permission set. */
  models: ConnectorSetModel[];
  /** Push 4-1 folder architecture: folders assigned to this permission set.
   *  Documents inside those folders inherit access via the RLS cascade. */
  folders: ConnectorSetFolder[];
}

export function fetchConnectorSets(): Promise<{ sets: ConnectorSet[] }> {
  return get('/api/admin/connector-sets');
}

/**
 * POST Reset Bootstrap — refreshes the Owner Bootstrap permission set from
 * the current catalog. Per D-147 refinement: only assignment rows reset;
 * underlying instance customizations (display_name overrides, settings)
 * are preserved.
 */
export interface ResetBootstrapResult {
  ok: true;
  templates_cloned_connectors: number;
  templates_cloned_prompts: number;
  templates_cloned_models: number;
  assignments_refreshed_connectors: number;
  assignments_refreshed_prompts: number;
  assignments_refreshed_models: number;
}
export function resetOwnerBootstrap(): Promise<ResetBootstrapResult> {
  return post('/api/admin/connector-sets/reset-bootstrap', {});
}

export function createConnectorSet(
  name: string,
  description?: string,
): Promise<{ ok: true; id: string }> {
  return post('/api/admin/connector-sets', {
    name,
    ...(description !== undefined && description !== '' ? { description } : {}),
  });
}

export function fetchConnectorSetDetail(setId: string): Promise<ConnectorSetDetail> {
  return get(`/api/admin/connector-sets/${encodeURIComponent(setId)}`);
}

export function updateConnectorSet(
  setId: string,
  patch: {
    name?: string;
    description?: string;
    /** Migration 0062: flip auto-assignment for new invitees. */
    auto_assign_new_members?: boolean;
  },
): Promise<{ ok: true }> {
  return fetch(`/api/admin/connector-sets/${encodeURIComponent(setId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(async (res) => {
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text === '' ? null : JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === 'object' && parsed !== null && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `PATCH /api/admin/connector-sets/${setId} failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true };
  });
}

export function deleteConnectorSet(setId: string): Promise<{ ok: true }> {
  return fetch(`/api/admin/connector-sets/${encodeURIComponent(setId)}`, {
    method: 'DELETE',
    credentials: 'include',
  }).then(async (res) => {
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text === '' ? null : JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === 'object' && parsed !== null && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `DELETE failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true };
  });
}

export function updateConnectorSetMembership(
  setId: string,
  patch: {
    add_users?: string[];
    remove_users?: string[];
    add_connectors?: string[];
    remove_connectors?: string[];
    // Migration 0037: prompts + models membership writes
    add_prompts?: string[];
    remove_prompts?: string[];
    add_models?: string[];
    remove_models?: string[];
    // Push 4-1 folder architecture: folder membership writes (Library pillar).
    add_folders?: string[];
    remove_folders?: string[];
  },
): Promise<{ ok: true }> {
  return post(`/api/admin/connector-sets/${encodeURIComponent(setId)}`, patch);
}

/* ---------------- Teammates (used by prompt sharing) ---------------- */

export interface Teammate {
  account_user_id: string;
  display_name: string;
  email: string | null;
  /**
   * Push #4.6 (D-187) — recipient-side opt-out. When false, share fns
   * (chat__share_session, knowledge__share_prompt, etc.) raise ERRCODE
   * 42501. UI surfaces affected teammates with a greyed-out row + tooltip
   * "Has disabled inbound sharing" rather than hiding them entirely, so
   * the sharer understands why someone isn't pickable.
   */
  allow_others_to_share_with_me: boolean;
}

export function fetchTeammates(): Promise<{ teammates: Teammate[] }> {
  return get('/api/me/teammates');
}
